import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Top-level error boundary. Before this, an uncaught render error anywhere
 * in the app unmounted the ENTIRE React tree with no visible message —
 * a blank white page and no way to diagnose it from the browser alone.
 * This catches that class of crash and shows the actual error instead.
 */
export class GlobalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('GlobalErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <div
          style={{
            padding: '40px',
            maxWidth: '900px',
            margin: '0 auto',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#111',
            background: '#fff',
            minHeight: '100vh',
          }}
        >
          <h1 style={{ color: '#c0392b', fontSize: '20px', marginBottom: '8px' }}>
            Something broke while rendering this page
          </h1>
          <p style={{ marginBottom: '16px' }}>{this.state.error.message}</p>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: '12px',
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflowX: 'auto',
            }}
          >
            {this.state.error.stack}
            {this.state.errorInfo?.componentStack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default GlobalErrorBoundary;
